import { Divider, Flex, Group, IconFont, Text } from '@byecode/ui'
import { DataSourceType, FilterMode } from '@lighthouse/core'
import {
    getAppointField,
    getTableIcon,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    ViewFieldFilterConfigure,
    ViewSortConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily,
    selectedBlockCustomViewAtom
} from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { ListItemMenu } from '../Common/ListItemMenu'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { ViewFieldSetting } from '../View/ViewField/ViewFieldSetting'

interface FieldGroupSettingProps {
    id: string
}

const SCxRequiredLabel = styled(Text)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    &.required {
        &::after {
            content: '*';
            margin-left: 4px;
            color: var(--color-red-500);
        }
    }
`

export const FieldGroupSetting: React.FunctionComponent<FieldGroupSettingProps> = () => {
    const { control, watch } = useFormContext()
    const pointer = watch('pointer')
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const styleProps = getBlockAndPageDesignLimit('fieldGroup')
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, pointer)
    const dataSourceList = useDataSourceList(appId, envId)
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const customViewDsId = useDeferredValue(
        useAtomData(
            selectedBlockCustomViewAtom,
            useCallback(s => s?.config.pointer, [])
        )
    )
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const columns = getViewColumns({
        tableProps: dataSource?.viewOptions.tableProps ?? [],
        schema: dataSource?.schema ?? {}
    })

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    const tipLabel = useMemo(() => {
        if (!pointer) {
            return (
                <>
                    <Text size={12} color="var(--color-red-500)">
                        无法设置字段，因为所在页面未指定数据表
                    </Text>
                    <IconFont color="var(--color-red-500)" style={{ marginLeft: 6 }} type="Warning" />
                </>
            )
        }
        if (dataSource) {
            return (
                <>
                    <Text size={12} color="var(--color-gray-500)">
                        字段来自
                    </Text>
                    <IconFont color="var(--color-gray-500)" style={{ margin: '0 4px 0 8px' }} type={getTableIcon(dataSource)} />
                    <Text size={12} color="var(--color-gray-500)">
                        {dataSource.name}
                    </Text>
                </>
            )
        }
        return null
    }, [dataSource, pointer])

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group
                        label="选择记录"
                        styles={{
                            collapse: {
                                padding: '0px 8px!important'
                            }
                        }}
                    >
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <SCxRequiredLabel className="required">记录来自</SCxRequiredLabel>
                            <Controller
                                name="pointer"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <SelectDataSource
                                        withinPortal
                                        placeholder="请选择数据表"
                                        value={value}
                                        onChange={onChange}
                                        // 表单编辑页可以使用用户表
                                        dataSourceList={dataSourceList}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        <Controller
                            name="filter"
                            control={control}
                            render={({ field: { onChange, value } }) => {
                                const filter = value
                                const filtersLength = filter?.expression?.conditions?.length || 0
                                return (
                                    <ListItemMenu
                                        width="auto"
                                        label={<SCxRequiredLabel className="required">查询条件</SCxRequiredLabel>}
                                        value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                        popupContent={
                                            <ViewFieldFilterConfigure
                                                dataSource={dataSource}
                                                dataSourceList={dataSourceList}
                                                pagePathParameter={pagePathParameter}
                                                pageQueryParameter={pageQueryParameter}
                                                pageVariables={pageVariables}
                                                pageStateParameter={pageStateParameter}
                                                availableFieldBlocksInfo={availableFieldBlocksInfo}
                                                availableFilterBlocksInfo={availableFilterBlocksInfo}
                                                viewDsId={customViewDsId}
                                                viewType="custom"
                                                columns={columns}
                                                paramsMode={FilterMode.CUSTOM}
                                                primaryField={primaryField}
                                                fieldMode={FilterMode.CUSTOM}
                                                filter={filter}
                                                noSettingInnerType={notDataSettingInnerTypes}
                                                onFilter={onChange}
                                                onMouseEnter={handleMouseEnter}
                                                onMouseLeave={handleMouseLeave}
                                            />
                                        }
                                    />
                                )
                            }}
                        />
                        <Controller
                            name="sorts"
                            render={({ field: { onChange, value } }) => (
                                <ListItemMenu
                                    width="auto"
                                    label="排序数据"
                                    value={Boolean(value?.length) && `${String(value?.length)} 条排序`}
                                    popupContent={
                                        <ViewSortConfigure
                                            noSettingInnerType={notDataSettingInnerTypes}
                                            columns={columns}
                                            primaryField={primaryField}
                                            sorters={value}
                                            onChangeSorter={onChange}
                                            isShowDsName={dataSource?.type === DataSourceType.joinDataSource}
                                        />
                                    }
                                />
                            )}
                        />
                    </Group>
                    <Divider />
                    <Group label="字段显隐">
                        {dataSource && (
                            <Controller
                                control={control}
                                name="viewFields"
                                render={({ field: { onChange, value } }) => {
                                    return (
                                        <ViewFieldSetting
                                            value={value}
                                            type={dataSource.type}
                                            schema={dataSource?.schema}
                                            viewType="table"
                                            tableProps={dataSource.viewOptions.tableProps}
                                            onChange={val => {
                                                onChange(val)
                                            }}
                                        />
                                    )
                                }}
                            />
                        )}
                        <Flex justifyContent="flex-end" px={8}>
                            {tipLabel}
                        </Flex>
                    </Group>
                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
